import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ChevronRight, 
  GitFork, 
  Plus, 
  Calendar, 
  User, 
  Code, 
  ExternalLink,
  Network,
  ArrowRight,
  Eye,
  Copy,
  X,
  Edit,
  GitBranch,
  GitPullRequest,
  Users,
  BarChart3
} from 'lucide-react';
import { 
  getForks, 
  forkPrompt, 
  getForkNetwork,
  checkForkAccess,
  getPrompt,
  getProjects
} from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import PromptTabs from '../components/PromptTabs';

const PromptForks = ({ user }) => {
  const { promptId } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [forks, setForks] = useState([]);
  const [forkNetwork, setForkNetwork] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForkModal, setShowForkModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [canFork, setCanFork] = useState(false);
  const [newFork, setNewFork] = useState({
    projectId: '',
    name: '',
    description: '',
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, [promptId]);

  const loadData = async () => {
    try {
      const [promptData, forksData, projectsData, accessData] = await Promise.all([
        getPrompt(promptId),
        getForks(promptId),
        getProjects(user.id),
        checkForkAccess(promptId, user.id)
      ]);
      setPrompt(promptData);
      setForks(forksData);
      setProjects(projectsData);
      setCanFork(accessData.can_fork);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadForkNetwork = async () => {
    try {
      const networkData = await getForkNetwork(promptId);
      setForkNetwork(networkData);
    } catch (error) {
      console.error('Failed to load fork network:', error);
    }
  };

  const handleForkPrompt = async () => {
    if (!newFork.projectId || !newFork.name.trim()) return;

    try {
      await forkPrompt({
        originalPromptId: promptId,
        newProjectId: newFork.projectId,
        newPromptName: newFork.name,
        newPromptDescription: newFork.description,
        forkReason: newFork.reason,
        userId: user.id
      });
      
      setShowForkModal(false);
      setNewFork({ projectId: '', name: '', description: '', reason: '' });
      loadData();
    } catch (error) {
      console.error('Failed to fork prompt:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Prompt not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">Forks</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Forks</h1>
          <p className="text-gray-600 mt-2">
            View and manage forks of {prompt.name}
          </p>
          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
            <div className="flex items-center">
              <GitFork className="h-4 w-4 mr-1" />
              {forks.length} fork{forks.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => {
              loadForkNetwork();
              setShowNetworkModal(true);
            }}
            className="btn-secondary"
          >
            <Network className="h-4 w-4 mr-2" />
            View Network
          </button>
          {canFork && (
            <button
              onClick={() => setShowForkModal(true)}
              className="btn-primary"
            >
              <GitFork className="h-4 w-4 mr-2" />
              Fork this Prompt
            </button>
          )}
        </div>
      </div>

      {/* Collaboration Navigation Tabs */}
      <PromptTabs promptId={promptId} />

      {/* Forks List */}
      <div className="card">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Forks of this prompt
          </h3>
        </div>
        
        {forks.length === 0 ? (
          <div className="text-center py-12">
            <GitFork className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No forks yet</h3>
            <p className="text-gray-600 mb-4">
              This prompt hasn't been forked yet. Be the first to create a fork!
            </p>
            {canFork && (
              <button
                onClick={() => setShowForkModal(true)}
                className="btn-primary"
              >
                Fork this prompt
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {forks.map((fork) => (
              <div key={fork.id} className="p-6">
                <ForkCard fork={fork} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fork Modal */}
      {showForkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fork Prompt</h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2 text-sm text-blue-800">
                  <Copy className="h-4 w-4" />
                  <span>You're creating a fork of <strong>{prompt.name}</strong></span>
                </div>
              </div>
              
              <div>
                <label htmlFor="targetProject" className="block text-sm font-medium text-gray-700 mb-1">
                  Target Project
                </label>
                <select
                  id="targetProject"
                  value={newFork.projectId}
                  onChange={(e) => setNewFork({ ...newFork, projectId: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="forkName" className="block text-sm font-medium text-gray-700 mb-1">
                  Prompt Name
                </label>
                <input
                  type="text"
                  id="forkName"
                  value={newFork.name}
                  onChange={(e) => setNewFork({ ...newFork, name: e.target.value })}
                  className="input"
                  placeholder={`${prompt.name} (forked)`}
                  required
                />
              </div>
              
              <div>
                <label htmlFor="forkDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  id="forkDescription"
                  value={newFork.description}
                  onChange={(e) => setNewFork({ ...newFork, description: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Describe your fork..."
                />
              </div>
              
              <div>
                <label htmlFor="forkReason" className="block text-sm font-medium text-gray-700 mb-1">
                  Fork Reason (optional)
                </label>
                <textarea
                  id="forkReason"
                  value={newFork.reason}
                  onChange={(e) => setNewFork({ ...newFork, reason: e.target.value })}
                  className="input"
                  rows={2}
                  placeholder="Why are you forking this prompt?"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleForkPrompt}
                className="btn-primary"
                disabled={!newFork.projectId || !newFork.name.trim()}
              >
                <GitFork className="h-4 w-4 mr-2" />
                Create Fork
              </button>
              <button
                onClick={() => {
                  setShowForkModal(false);
                  setNewFork({ projectId: '', name: '', description: '', reason: '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fork Network Modal */}
      {showNetworkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Fork Network</h3>
              <button
                onClick={() => setShowNetworkModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {forkNetwork.length === 0 ? (
                <div className="text-center py-8">
                  <Network className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No fork network data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {forkNetwork.map((item, index) => (
                    <NetworkNode key={item.id} node={item} isRoot={index === 0} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ForkCard = ({ fork }) => {
  return (
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center space-x-3 mb-2">
          <Link 
            to={`/prompts/${fork.forked_prompt_id}`}
            className="text-lg font-semibold text-primary-600 hover:text-primary-700"
          >
            <GitFork className="h-4 w-4 inline mr-1" />
            {fork.forked_prompt_name}
          </Link>
          <ArrowRight className="h-4 w-4 text-gray-400" />
          <span className="text-gray-600">{fork.forked_project_name}</span>
        </div>
        
        {fork.forked_prompt_description && (
          <p className="text-gray-600 mb-3">{fork.forked_prompt_description}</p>
        )}
        
        {fork.fork_reason && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Fork reason:</strong> {fork.fork_reason}
            </p>
          </div>
        )}
        
        <div className="flex items-center space-x-6 text-sm text-gray-500">
          <div className="flex items-center">
            <Code className="h-4 w-4 mr-1" />
            {fork.version_count} version{fork.version_count !== 1 ? 's' : ''}
          </div>
          
          <div className="flex items-center">
            <User className="h-4 w-4 mr-1" />
            {fork.forked_by_name}
          </div>
          
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            Forked {formatDistanceToNow(new Date(fork.created_at), { addSuffix: true })}
          </div>
          
          {fork.last_activity && (
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Updated {formatDistanceToNow(new Date(fork.last_activity), { addSuffix: true })}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-2 ml-4">
        <Link
          to={`/prompts/${fork.forked_prompt_id}`}
          className="btn-secondary text-sm"
        >
          <Eye className="h-4 w-4 mr-1" />
          View Fork
        </Link>
      </div>
    </div>
  );
};

const NetworkNode = ({ node, isRoot }) => {
  return (
    <div className={`flex items-center space-x-4 p-4 rounded-lg ${isRoot ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'}`}>
      <div className="flex items-center space-x-2" style={{ marginLeft: `${node.fork_level * 24}px` }}>
        {!isRoot && <ArrowRight className="h-4 w-4 text-gray-400" />}
        <div className={`w-3 h-3 rounded-full ${isRoot ? 'bg-blue-500' : 'bg-gray-400'}`}></div>
      </div>
      
      <div className="flex-1">
        <div className="flex items-center space-x-2">
          <Link 
            to={`/prompts/${node.id}`}
            className="font-medium text-primary-600 hover:text-primary-700"
          >
            {node.name}
          </Link>
          {isRoot && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Original</span>
          )}
        </div>
        <div className="text-sm text-gray-600">{node.project_name}</div>
        {node.description && (
          <div className="text-sm text-gray-500 mt-1">{node.description}</div>
        )}
      </div>
      
      <div className="text-right text-sm text-gray-500">
        <div>{node.owner_name}</div>
        <div className="flex items-center space-x-2">
          <Code className="h-3 w-3" />
          <span>{node.version_count} versions</span>
        </div>
        {node.fork_created_at && (
          <div className="text-xs">
            Forked {formatDistanceToNow(new Date(node.fork_created_at), { addSuffix: true })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptForks; 